import { useState, useMemo, useCallback } from 'react';

/**
 * Virtual List Hook
 * Provides efficient rendering for large lists by only rendering visible items
 */

// =============================================================================
// Virtual List Configuration
// =============================================================================

export interface UseVirtualListProps {
  itemCount: number;
  itemHeight: number;
  containerHeight: number;
  overscan?: number;
}

export interface VirtualListResult {
  visibleRange: {
    startIndex: number;
    endIndex: number;
  };
  totalHeight: number;
  onScroll: (e: React.UIEvent<HTMLDivElement>) => void;
  getItemStyle: (index: number) => React.CSSProperties;
}

// =============================================================================
// Main Virtual List Hook
// =============================================================================

/**
 * Virtual list hook for efficient rendering of large lists
 * @param config - Virtual list configuration
 * @returns Virtual list utilities and handlers
 */
export const useVirtualList = ({
  itemCount,
  itemHeight,
  containerHeight,
  overscan = 5,
}: UseVirtualListProps): VirtualListResult => {
  const [scrollTop, setScrollTop] = useState(0);

  const visibleRange = useMemo(() => {
    const visibleItemCount = Math.ceil(containerHeight / itemHeight);
    const startIndex = Math.floor(scrollTop / itemHeight);
    const endIndex = Math.min(startIndex + visibleItemCount + overscan, itemCount - 1);

    return {
      startIndex: Math.max(0, startIndex - overscan),
      endIndex,
    };
  }, [scrollTop, itemHeight, containerHeight, itemCount, overscan]);

  const totalHeight = itemCount * itemHeight;

  const onScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(e.currentTarget.scrollTop);
  }, []);

  const getItemStyle = useCallback(
    (index: number): React.CSSProperties => {
      return {
        position: 'absolute',
        top: index * itemHeight,
        left: 0,
        right: 0,
        height: itemHeight,
      };
    },
    [itemHeight]
  );

  return {
    visibleRange,
    totalHeight,
    onScroll,
    getItemStyle,
  };
};

// =============================================================================
// Variable Height Virtual List Hook
// =============================================================================

export interface UseVariableVirtualListProps {
  itemCount: number;
  getItemHeight: (index: number) => number;
  containerHeight: number;
  overscan?: number;
}

export interface VariableVirtualListResult {
  visibleRange: {
    startIndex: number;
    endIndex: number;
  };
  totalHeight: number;
  onScroll: (e: React.UIEvent<HTMLDivElement>) => void;
  getItemStyle: (index: number) => React.CSSProperties;
}

/**
 * Virtual list hook for items with variable heights
 * @param config - Variable virtual list configuration
 * @returns Variable virtual list utilities and handlers
 */
export const useVariableVirtualList = ({
  itemCount,
  getItemHeight,
  containerHeight,
  overscan = 5,
}: UseVariableVirtualListProps): VariableVirtualListResult => {
  const [scrollTop, setScrollTop] = useState(0);

  // Pre-calculate item positions and total height
  const { itemPositions, totalHeight } = useMemo(() => {
    const positions: number[] = [];
    let currentTop = 0;

    for (let i = 0; i < itemCount; i++) {
      positions[i] = currentTop;
      currentTop += getItemHeight(i);
    }

    return {
      itemPositions: positions,
      totalHeight: currentTop,
    };
  }, [itemCount, getItemHeight]);

  const visibleRange = useMemo(() => {
    // Binary search to find first visible item
    let startIndex = 0;
    let endIndex = itemCount - 1;

    while (startIndex < endIndex) {
      const midIndex = Math.floor((startIndex + endIndex) / 2);
      const midPosition = itemPositions[midIndex];

      if (midPosition < scrollTop) {
        startIndex = midIndex + 1;
      } else {
        endIndex = midIndex;
      }
    }

    // Find last visible item
    let lastVisibleIndex = startIndex;
    const currentBottom = scrollTop + containerHeight;

    while (lastVisibleIndex < itemCount && itemPositions[lastVisibleIndex] < currentBottom) {
      lastVisibleIndex++;
    }

    return {
      startIndex: Math.max(0, startIndex - overscan),
      endIndex: Math.min(lastVisibleIndex + overscan, itemCount - 1),
    };
  }, [scrollTop, containerHeight, itemPositions, itemCount, overscan]);

  const onScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(e.currentTarget.scrollTop);
  }, []);

  const getItemStyle = useCallback(
    (index: number): React.CSSProperties => {
      return {
        position: 'absolute',
        top: itemPositions[index],
        left: 0,
        right: 0,
        height: getItemHeight(index),
      };
    },
    [itemPositions, getItemHeight]
  );

  return {
    visibleRange,
    totalHeight,
    onScroll,
    getItemStyle,
  };
};

// =============================================================================
// Grid Virtual List Hook
// =============================================================================

export interface UseVirtualGridProps {
  rowCount: number;
  columnCount: number;
  rowHeight: number;
  columnWidth: number;
  containerHeight: number;
  containerWidth: number;
  overscan?: number;
}

export interface VirtualGridResult {
  visibleRange: {
    startRowIndex: number;
    endRowIndex: number;
    startColumnIndex: number;
    endColumnIndex: number;
  };
  totalHeight: number;
  totalWidth: number;
  onScroll: (e: React.UIEvent<HTMLDivElement>) => void;
  getItemStyle: (rowIndex: number, columnIndex: number) => React.CSSProperties;
}

/**
 * Virtual grid hook for efficient rendering of 2D grids
 * @param config - Virtual grid configuration
 * @returns Virtual grid utilities and handlers
 */
export const useVirtualGrid = ({
  rowCount,
  columnCount,
  rowHeight,
  columnWidth,
  containerHeight,
  containerWidth,
  overscan = 2,
}: UseVirtualGridProps): VirtualGridResult => {
  const [scrollTop, setScrollTop] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const visibleRange = useMemo(() => {
    const visibleRowCount = Math.ceil(containerHeight / rowHeight);
    const visibleColumnCount = Math.ceil(containerWidth / columnWidth);

    const startRowIndex = Math.floor(scrollTop / rowHeight);
    const startColumnIndex = Math.floor(scrollLeft / columnWidth);

    return {
      startRowIndex: Math.max(0, startRowIndex - overscan),
      endRowIndex: Math.min(startRowIndex + visibleRowCount + overscan, rowCount - 1),
      startColumnIndex: Math.max(0, startColumnIndex - overscan),
      endColumnIndex: Math.min(startColumnIndex + visibleColumnCount + overscan, columnCount - 1),
    };
  }, [
    scrollTop,
    scrollLeft,
    rowHeight,
    columnWidth,
    containerHeight,
    containerWidth,
    rowCount,
    columnCount,
    overscan,
  ]);

  const totalHeight = rowCount * rowHeight;
  const totalWidth = columnCount * columnWidth;

  const onScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(e.currentTarget.scrollTop);
    setScrollLeft(e.currentTarget.scrollLeft);
  }, []);

  const getItemStyle = useCallback(
    (rowIndex: number, columnIndex: number): React.CSSProperties => {
      return {
        position: 'absolute',
        top: rowIndex * rowHeight,
        left: columnIndex * columnWidth,
        height: rowHeight,
        width: columnWidth,
      };
    },
    [rowHeight, columnWidth]
  );

  return {
    visibleRange,
    totalHeight,
    totalWidth,
    onScroll,
    getItemStyle,
  };
};
